import type { LinkSnapshot, TaskSnapshot } from '../stores/types';
import { parsePredecessorInput, resolvePredecessorTaskIds, nextClientLinkId } from './addTaskUtils';
import { resolveTaskName } from '../utils/utils';
import {
  dependencyTokenToDhtmlxType,
  formatDependencyLinkToken,
  parseDependencyTokenList,
  type ParsedDependencyToken,
} from './dependencyDisplayUtils';
import { validatePredecessorLink } from './predecessorValidation';

/** Best available WBS code for a linked task (srNo from API, then wbs). */
export function resolveTaskWbsCode(task: { srNo?: unknown; wbs?: unknown } | null | undefined): string {
  if (!task) return '';
  const srNo = task.srNo != null ? String(task.srNo).trim() : '';
  if (srNo) return srNo;
  const wbs = task.wbs != null ? String(task.wbs).trim() : '';
  return wbs;
}

/** e.g. "Predecessor (1.2, 1.3)" when WBS codes are known. */
export function formatDependencyFieldLabel(baseLabel: string, wbsCodes: string[]): string {
  const codes = wbsCodes.map((code) => code.trim()).filter(Boolean);
  return codes.length > 0 ? `${baseLabel} (${codes.join(', ')})` : baseLabel;
}

function linkDisplayCode(
  task: TaskSnapshot | null | undefined,
  fallbackId: string,
): string {
  return (
    resolveTaskWbsCode(task) ||
    resolveTaskName(task) ||
    String(task?.wbs ?? '') ||
    fallbackId
  );
}

/** Display text for one dependency link including type + lag (e.g. `1.2FS+2`). */
export function formatLinkDependencyDisplay(
  link: { source: string | number; target: string | number; type?: string | number | null; lag?: number | null },
  relatedTask: TaskSnapshot | null | undefined,
  relatedId: string,
): string {
  return formatDependencyLinkToken(linkDisplayCode(relatedTask, relatedId), String(link.type ?? '0'), link.lag);
}

/** Display text for predecessor field (Sr No preferred, then name) with type + lag. */
export function buildPredecessorDisplayText(
  taskId: string,
  links: LinkSnapshot[],
  getTask: (id: string) => TaskSnapshot | null | undefined,
): string {
  return links
    .filter((link) => String(link.target) === String(taskId))
    .map((link) => formatLinkDependencyDisplay(link, getTask(String(link.source)), String(link.source)))
    .filter(Boolean)
    .join(', ');
}

/** Display text for successor field with type + lag. */
export function buildSuccessorDisplayText(
  taskId: string,
  links: LinkSnapshot[],
  getTask: (id: string) => TaskSnapshot | null | undefined,
): string {
  return links
    .filter((link) => String(link.source) === String(taskId))
    .map((link) => formatLinkDependencyDisplay(link, getTask(String(link.target)), String(link.target)))
    .filter(Boolean)
    .join(', ');
}

/** Resolve a single predecessor token to a task, if possible. */
export function resolvePredecessorTokenToTask(
  token: string,
  tasks: TaskSnapshot[],
  resolveWbsCode?: (taskId: string) => string | null,
): TaskSnapshot | undefined {
  const parsed = parseDependencyTokenList(token)[0];
  const clean = parsed?.taskKey ?? token.replace(/\s*(FS|SS|FF|SF)\s*$/i, '').trim();
  if (!clean) return undefined;
  const [id] = resolvePredecessorTaskIds([clean], tasks, resolveWbsCode);
  if (!id) return undefined;
  return tasks.find((task) => String(task.id) === String(id));
}

/** Display token for a predecessor (Sr No preferred, then name; keeps type/lag when present). */
export function predecessorTokenDisplayName(
  token: string,
  tasks: TaskSnapshot[],
  resolveWbsCode?: (taskId: string) => string | null,
): string {
  const parsed = parseDependencyTokenList(token)[0];
  const task = resolvePredecessorTokenToTask(token, tasks, resolveWbsCode);
  if (!task) return token;
  const code = resolveTaskWbsCode(task) || resolveTaskName(task) || String(task.id);
  return formatDependencyLinkToken(code, parsed?.type ?? 'FS', parsed?.lag ?? 0);
}

/** Replace resolvable tokens with Sr No / display codes including type + lag. */
export function normalizePredecessorDraftInput(
  input: string,
  tasks: TaskSnapshot[],
  resolveWbsCode?: (taskId: string) => string | null,
): string {
  const tokens = parseDependencyTokenList(input);
  if (tokens.length === 0) return input.trim();
  return tokens
    .map((parsed) => {
      const [id] = resolvePredecessorTaskIds([parsed.taskKey], tasks, resolveWbsCode);
      const task = id ? tasks.find((t) => String(t.id) === String(id)) : undefined;
      if (!task) {
        return formatDependencyLinkToken(parsed.taskKey, parsed.type, parsed.lag);
      }
      const code = resolveTaskWbsCode(task) || resolveTaskName(task) || String(task.id);
      return formatDependencyLinkToken(code, parsed.type, parsed.lag);
    })
    .join(', ');
}

/** True when two draft strings resolve to the same predecessor ids + type + lag. */
export function predecessorDraftInputsEquivalent(
  a: string,
  b: string,
  tasks: TaskSnapshot[],
  resolveWbsCode?: (taskId: string) => string | null,
): boolean {
  const keyOf = (input: string) =>
    parseDependencyTokenList(input)
      .map((parsed) => {
        const [id] = resolvePredecessorTaskIds([parsed.taskKey], tasks, resolveWbsCode);
        return `${String(id ?? parsed.taskKey)}|${parsed.type}|${parsed.lag}`;
      })
      .sort()
      .join(',');
  return keyOf(a) === keyOf(b);
}

/** Label with WBS codes derived from editable predecessor input. */
export function buildPredecessorFieldLabel(
  input: string,
  tasks: TaskSnapshot[],
  resolveWbsCode?: (taskId: string) => string | null,
): string {
  const tokens = parseDependencyTokenList(input);
  if (tokens.length === 0) return 'Predecessor';
  const wbsCodes = tokens
    .map((parsed) => {
      const [id] = resolvePredecessorTaskIds([parsed.taskKey], tasks, resolveWbsCode);
      if (!id) return '';
      return resolveTaskWbsCode(tasks.find((task) => String(task.id) === String(id)));
    })
    .filter(Boolean);
  return formatDependencyFieldLabel('Predecessor', wbsCodes);
}

export interface PredecessorLinkChange {
  addedLinkIds: Array<number | string>;
  removedLinkIds: Array<number | string>;
  /** Task ids touched by the link change (target, predecessors, removed link endpoints). */
  affectedTaskIds: string[];
  /** Full snapshots for links being added (avoids store lookup after mid-sync remaps). */
  addedLinks: Array<{
    id: number | string;
    source: string;
    target: string;
    type: string;
    lag: number | null;
  }>;
  /** Endpoints for removed links (needed to delete by source/target when gantt ids differ). */
  removedLinks: Array<{ id: number | string; source: string; target: string }>;
}

function uniqueTaskIds(ids: string[]): string[] {
  return [...new Set(ids.map(String).filter(Boolean))];
}

type DesiredPredecessor = ParsedDependencyToken & { sourceId: string };

/** Sync store links to match predecessor input (Sr No, WBS, name, or task id tokens + type/lag). */
export function applyPredecessorInputToStore(params: {
  taskId: string;
  input: string;
  tasks: TaskSnapshot[];
  links: LinkSnapshot[];
  resolveWbsCode?: (taskId: string) => string | null;
  addLink: (link: LinkSnapshot) => void;
  removeLink: (id: number | string) => void;
}): PredecessorLinkChange {
  const taskId = String(params.taskId);
  const desired: DesiredPredecessor[] = [];
  const seenSources = new Set<string>();

  for (const parsed of parseDependencyTokenList(params.input)) {
    const [sourceId] = resolvePredecessorTaskIds(
      [parsed.taskKey],
      params.tasks,
      params.resolveWbsCode,
    ).filter((id) => String(id) !== taskId);
    if (!sourceId || seenSources.has(String(sourceId))) continue;
    seenSources.add(String(sourceId));
    desired.push({ ...parsed, sourceId: String(sourceId) });
  }

  // Validate against the graph as it will exist after this replace (drop current
  // preds of this task, then accept desired sources one-by-one).
  const provisionalLinks: Array<{ source: string; target: string }> = params.links
    .filter((link) => String(link.target) !== taskId)
    .map((link) => ({ source: String(link.source), target: String(link.target) }));
  for (const item of desired) {
    const check = validatePredecessorLink(
      item.sourceId,
      taskId,
      params.tasks,
      provisionalLinks,
    );
    if (!check.ok) {
      throw new Error(check.reason);
    }
    provisionalLinks.push({ source: item.sourceId, target: taskId });
  }

  const currentLinks = params.links.filter((link) => String(link.target) === taskId);
  const desiredBySource = new Map(desired.map((d) => [d.sourceId, d]));

  const removedLinks: LinkSnapshot[] = [];
  const toReplace: DesiredPredecessor[] = [];

  for (const link of currentLinks) {
    const want = desiredBySource.get(String(link.source));
    if (!want) {
      removedLinks.push(link);
      continue;
    }
    const currentType = String(link.type ?? '0');
    const wantType = dependencyTokenToDhtmlxType(want.type);
    const currentLag = Number(link.lag) || 0;
    if (currentType !== wantType || currentLag !== want.lag) {
      removedLinks.push(link);
      toReplace.push(want);
    }
  }

  const currentSourceIds = new Set(currentLinks.map((link) => String(link.source)));
  const toAdd = [
    ...desired.filter((d) => !currentSourceIds.has(d.sourceId)),
    ...toReplace,
  ];

  const removedLinkIds = removedLinks.map((link) => link.id);
  for (const id of removedLinkIds) {
    params.removeLink(id);
  }

  let linkId = nextClientLinkId(params.links);
  const addedLinkIds: Array<number | string> = [];
  const addedLinks: PredecessorLinkChange['addedLinks'] = [];

  for (const item of toAdd) {
    const snap: PredecessorLinkChange['addedLinks'][number] = {
      id: linkId,
      source: item.sourceId,
      target: taskId,
      type: dependencyTokenToDhtmlxType(item.type),
      lag: item.lag === 0 ? null : item.lag,
    };
    params.addLink(snap);
    addedLinkIds.push(linkId);
    addedLinks.push(snap);
    linkId += 1;
  }

  const affectedTaskIds = uniqueTaskIds([
    taskId,
    ...toAdd.map((d) => d.sourceId),
    ...removedLinks.flatMap((link) => [String(link.source), String(link.target)]),
  ]);

  return {
    addedLinkIds,
    removedLinkIds,
    affectedTaskIds,
    addedLinks,
    removedLinks: removedLinks.map((link) => ({
      id: link.id,
      source: String(link.source),
      target: String(link.target),
    })),
  };
}

function linkEndpointPairKey(source: string | number, target: string | number): string {
  return `${String(source)}\0${String(target)}`;
}

/** Find a gantt link by predecessor → successor endpoints. */
export function findGanttLinkByEndpoints(
  gantt: {
    getLinks: () => Array<{
      id: number | string;
      source: string | number;
      target: string | number;
      type?: string | number;
      lag?: number | null;
    }>;
  },
  source: string,
  target: string,
): LinkSnapshot | null {
  const match = gantt.getLinks().find(
    (link) => String(link.source) === String(source) && String(link.target) === String(target),
  );
  if (!match) return null;
  return {
    id: match.id,
    source: String(match.source),
    target: String(match.target),
    type: String(match.type ?? '0'),
    lag: match.lag ?? null,
  };
}

/**
 * Merge gantt links into the store snapshot without dropping store-only links
 * that have not been painted into gantt yet.
 */
export function mergeLinkSnapshotsFromGantt(
  storeLinks: LinkSnapshot[],
  ganttLinks: LinkSnapshot[],
): LinkSnapshot[] {
  const merged = new Map<string, LinkSnapshot>();
  for (const link of storeLinks) {
    merged.set(linkEndpointPairKey(link.source, link.target), link);
  }
  for (const link of ganttLinks) {
    const key = linkEndpointPairKey(link.source, link.target);
    const existing = merged.get(key);
    const numericId = Number(link.id);
    const id =
      Number.isFinite(numericId) && String(numericId) === String(link.id).trim()
        ? numericId
        : link.id;
    const normalized: LinkSnapshot = {
      id,
      source: String(link.source),
      target: String(link.target),
      type: String(link.type ?? '0'),
      lag: link.lag ?? null,
    };
    merged.set(key, existing ? { ...existing, ...normalized } : normalized);
  }
  return [...merged.values()];
}

/** Resolve session affected link ids after gantt sync (gantt ids win for new links). */
export function resolvePredecessorAffectedLinkIds(
  change: PredecessorLinkChange,
  options: {
    ganttLinksAlreadySynced: boolean;
    findLinkByEndpoints: (source: string, target: string) => LinkSnapshot | null;
    getStoreLink: (id: number | string) => LinkSnapshot | undefined;
  },
): { removed: Array<number | string>; added: Array<number | string> } {
  const added: Array<number | string> = [];
  for (let i = 0; i < change.addedLinkIds.length; i++) {
    const clientId = change.addedLinkIds[i];
    const snap = change.addedLinks[i] ?? options.getStoreLink(clientId);
    if (!snap || options.ganttLinksAlreadySynced) {
      added.push(clientId);
      continue;
    }
    const inGantt = options.findLinkByEndpoints(snap.source, snap.target);
    added.push(inGantt?.id ?? clientId);
  }
  return { removed: [...change.removedLinkIds], added };
}

/** Repopulate dhtmlx task.$source / $target from the current gantt link list. */
export function repopulateGanttLinkPointers(gantt: {
  getLinks: () => Array<{ id: number | string; source: string | number; target: string | number }>;
  eachTask: (cb: (task: { $source?: unknown[]; $target?: unknown[] }) => void) => void;
  isTaskExists: (id: string | number) => boolean;
  getTask: (id: string | number) => { $source?: unknown[]; $target?: unknown[] };
}): void {
  const allLinks = gantt.getLinks();
  gantt.eachTask((task) => {
    task.$source = [];
    task.$target = [];
  });
  for (const link of allLinks) {
    if (gantt.isTaskExists(link.source)) {
      const src = gantt.getTask(link.source);
      src.$source = src.$source || [];
      if (src.$source.indexOf(link.id) === -1 && src.$source.indexOf(String(link.id)) === -1) {
        src.$source.push(link.id);
      }
    }
    if (gantt.isTaskExists(link.target)) {
      const tgt = gantt.getTask(link.target);
      tgt.$target = tgt.$target || [];
      if (tgt.$target.indexOf(link.id) === -1 && tgt.$target.indexOf(String(link.id)) === -1) {
        tgt.$target.push(link.id);
      }
    }
  }
}
